import {
  IGlobalSchema,
  SchemaBatch,
  EEventTypes,
  ITrackCartEventPayload,
  ITrackCheckoutStepPayload,
  ITrackPageViewPayload,
  ITrackClickEventPayload,
} from '../interfaces'
import { pushData, sendBeacon } from './pushData'

class YaloAnalytics {
  private constructor() {}
  private static instance: YaloAnalytics

  // all the events that are going to be sent to the server
  private callsQueue: SchemaBatch = []
  private errorCallsQueue: SchemaBatch = []

  private isQueueRunning: boolean = false
  private isErrorsQueueRunning: boolean = false

  private queueId: ReturnType<typeof setTimeout> | null = null

  private errorsCount: number = 0

  // delay time after events are called
  public timeDelay: number = 10000

  // size of the batch that is going to be sent
  public batchSize: number = 3

  public errorRateLimit: number = 20
  public backOffTime: number = 2000
  public backOffTimeLimit: number = 64000
  public static trackEventsURL = ''
  public static trackEvents = false

  public static getInstance(): YaloAnalytics {
    if (!YaloAnalytics.instance) {
      YaloAnalytics.instance = new YaloAnalytics()
    }
    window.addEventListener('beforeunload', () => {
      this.instance.cancelTimeouts()
    })
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.instance.cancelTimeouts()
      }
    })

    return YaloAnalytics.instance
  }

  public static trackClickEvent(data: ITrackClickEventPayload): void {
    const {
      eventName,
      phoneNumber,
      sessionId,
      viewName,
      storefront,
      extraIds,
      payload,
    } = data
    const eventData: IGlobalSchema = {
      eventType: EEventTypes.INTERACTION,
      eventName,
      viewName,
      sessionId,
      phoneNumber,
      storefront,
      errors: [],
      extraIds,
      payload,
    }
    const instance = YaloAnalytics.getInstance()
    instance.enQueue(eventData)
  }

  // track a cart event
  public static trackCartEvent(data: ITrackCartEventPayload): void {
    const {
      cartData: payload,
      eventName,
      phoneNumber,
      sessionId,
      viewName,
      storefront,
      extraIds,
    } = data
    const eventData: IGlobalSchema = {
      eventType: EEventTypes.INTERACTION,
      eventName,
      viewName,
      sessionId,
      phoneNumber,
      payload,
      storefront,
      errors: [],
      extraIds,
    }
    const instance = YaloAnalytics.getInstance()
    instance.enQueue(eventData)
  }
  // track a page view event
  public static trackPageView(data: ITrackPageViewPayload): void {
    const {
      pageData: payload,
      eventName,
      phoneNumber,
      sessionId,
      viewName,
      extraIds,
      storefront,
    } = data
    const eventData: IGlobalSchema = {
      eventType: EEventTypes.INTERACTION,
      eventName,
      viewName,
      sessionId,
      phoneNumber,
      payload,
      errors: [],
      extraIds,
      storefront,
    }
    const url = YaloAnalytics.trackEventsURL
    sendBeacon([eventData], url)
  }
  // track checkout steps
  public static trackCheckoutStep(data: ITrackCheckoutStepPayload): void {
    const {
      stepData: payload,
      eventName,
      phoneNumber,
      sessionId,
      viewName,
      extraIds,
    } = data
    const instance = YaloAnalytics.getInstance()
    const eventData: IGlobalSchema = {
      eventType: EEventTypes.INTERACTION,
      eventName,
      viewName,
      sessionId,
      phoneNumber,
      payload,
      errors: [],
      extraIds,
    }
    instance.enQueue(eventData)
  }

  // add a event to the queue
  private enQueue(data: IGlobalSchema): void {
    if (!YaloAnalytics.trackEvents) return
    this.callsQueue.push(data)
    // prevent duplicated calls
    if (!this.isQueueRunning) {
      // starts the execution of the queue
      this.startQueue()
    }
  }

  private startQueue(): void {
    this.isQueueRunning = true
    // saves the id of the timeout to be able to cancel it
    this.queueId = setTimeout(() => {
      this.cleanQueue()
    }, this.timeDelay)
  }

  private delay = (ms: number): Promise<void> =>
    new Promise((res) => setTimeout(res, ms))

  private async sendErrorsQueue(): Promise<void> {
    while (this.errorCallsQueue.length > 0) {
      const events = this.errorCallsQueue.splice(0, this.batchSize)
      if (events) {
        const url = YaloAnalytics.trackEventsURL
        try {
          await pushData(events, url)
        } catch (error) {
          if (this.backOffTime < this.backOffTimeLimit) {
            this.errorCallsQueue.push(...events)
            await this.delay(this.backOffTime)
            this.backOffTime *= 2
          } else {
            this.errorCallsQueue = []
            this.backOffTime = 2000
            this.isErrorsQueueRunning = false
          }
        }
      }
    }
  }
  // clean queue by sending events
  private async cleanQueue(withFetch: boolean = true): Promise<void> {
    while (this.callsQueue.length > 0) {
      const events = this.callsQueue.splice(0, this.batchSize)
      const url = YaloAnalytics.trackEventsURL
      if (events) {
        try {
          // eslint-disable-next-line no-await-in-loop
          if (withFetch) await pushData(events, url)
          else sendBeacon(events, url)
        } catch (error) {
          console.error('Error sending data to analytics', error)
          this.errorCallsQueue.push(...events)
          if (!this.isErrorsQueueRunning) {
            this.sendErrorsQueue()
          }
        }
      }
    }
    this.isQueueRunning = false
  }

  private cancelTimeouts() {
    this.isQueueRunning = false
    clearTimeout(this.queueId as ReturnType<typeof setTimeout>)
    this.cleanQueue(false)
  }
}

export default YaloAnalytics
